import humanizeDuration from 'humanize-duration';

/**
 * Prints a date in format mm/dd if it is in the current year, or otherwise mm/dd/YYYY.
 *
 * @param d The date to format.
 */
export function toDateString(d: Date): string {
  // We would prefer to use the Intl API here, but that would print dates in the localized time and not UTC.
  // Once we update the aggregation on the backend to support different timezones we should update this fn.
  if (d.getFullYear() !== new Date().getFullYear()) {
    return d.getUTCMonth() + 1 + '/' + d.getUTCDate() + '/' + d.getUTCFullYear();
  }
  return d.getUTCMonth() + 1 + '/' + d.getUTCDate();
}

/**
 * Formats the date in the format mm dd, yyyy / hh:mm TZ.
 *
 * @param d date to format.
 * @param options optional format options to pass to Intl.DateTimeFormat.
 */
export function datetimeFormat(d?: number | Date, options?: Intl.DateTimeFormatOptions) {
  const date = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' } as Intl.DateTimeFormatOptions).format(d);
  const time = new Intl.DateTimeFormat('en-us', {
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
    ...options,
  }).format(d);

  return `${date} / ${time}`;
}

/**
 * Formats the duration in the short format: 361000 becomes "6m 1s".
 */
export const shortEnglishHumanizer = humanizeDuration.humanizer({
  delimiter: ' ',
  language: 'shortEn',
  languages: {
    shortEn: {
      y: () => 'y',
      mo: () => 'mo',
      w: () => 'w',
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
      s: () => 's',
      ms: () => 'ms',
    },
  },
  spacer: '',
});

/**
 * Formats the distance between the given date and now in words, abbreviated.
 */
export function formatDistanceToNowShort(date: Date | number) {
  const duration = Date.now() - date.valueOf();
  const result = shortEnglishHumanizer(duration, {
    largest: 1,
    round: true,
  });

  if (result === '0s') {
    return 'now';
  }

  if (duration > 0) {
    return `${result} ago`;
  }

  return `in ${result}`;
}

/**
 * Formats the distance between the given date without any label.
 */
export function formatDistanceToNow(date: Date | number) {
  const duration = Date.now() - date.valueOf();
  return shortEnglishHumanizer(duration, {
    largest: 1,
    round: true,
  });
}
